import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiMic, FiArrowRight, FiCheck, FiInfo, FiPhone, FiZap, FiMessageSquare } = FiIcons;

const useCases = [
  {
    icon: FiPhone,
    title: 'Field tech calls in an equipment issue',
    desc: 'The AI agent answers, captures asset ID, issue type, and urgency — then creates a JSM ticket automatically with all fields populated.',
  },
  {
    icon: FiZap,
    title: 'After-hours intake without a human on call',
    desc: 'Voice agent handles inbound requests 24/7. Tickets are created, categorized, and queued for the morning shift with no manual entry.',
  },
  {
    icon: FiMessageSquare,
    title: 'Reduce portal friction for field teams',
    desc: 'Some techs will never log into a portal. A phone call is faster. AI voice intake meets them where they are.',
  },
];

const included = [
  'AI voice agent scoped and configured for your intake flow',
  'Integration with your JSM environment',
  'Asset lookup and ticket creation on call completion',
  'Routing logic tied to your existing queues and SLAs',
  'Handoff to live agent when escalation is needed',
];

const HomeAIVoice = () => {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section id="ai-voice" className="py-24" style={{ background: '#ffffff' }}>
      <div className="max-w-5xl mx-auto px-8 lg:px-16">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded text-xs font-bold uppercase tracking-widest mb-5"
            style={{ background: '#dcfce7', color: '#16a34a', border: '1px solid #86efac' }}>
            Optional Add-On
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#052e16' }}>
            AI voice intake — for teams that won't use a portal.
          </h2>
          <p className="text-base leading-relaxed max-w-2xl" style={{ color: '#374151' }}>
            Once your JSM system is live, some field techs and site managers still won't submit tickets through a portal. An AI voice agent lets them call in — and the ticket gets created automatically, linked to the right asset, and routed to the right queue.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-5 mb-10">
          {useCases.map((u, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="p-6 rounded-xl border" style={{ background: '#f0fdf4', borderColor: '#bbf7d0' }}>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-4" style={{ background: '#dcfce7' }}>
                <SafeIcon icon={u.icon} className="w-4 h-4" style={{ color: '#16a34a' }} />
              </div>
              <h3 className="text-sm font-bold mb-2" style={{ color: '#052e16' }}>{u.title}</h3>
              <p className="text-sm leading-relaxed" style={{ color: '#166534' }}>{u.desc}</p>
            </motion.div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* What's included */}
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="p-7 rounded-xl border" style={{ background: '#052e16', borderColor: '#166534' }}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(74,222,128,0.15)' }}>
                <SafeIcon icon={FiMic} className="w-4 h-4" style={{ color: '#4ade80' }} />
              </div>
              <div>
                <h3 className="text-base font-bold text-white">What's included</h3>
                <span className="text-xs" style={{ color: '#86efac' }}>Scoped separately from the core build</span>
              </div>
            </div>
            <div className="space-y-3">
              {included.map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <SafeIcon icon={FiCheck} className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: '#4ade80' }} />
                  <span className="text-sm leading-relaxed" style={{ color: '#bbf7d0' }}>{item}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Pricing + CTA */}
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ delay: 0.08 }} className="flex flex-col gap-4">

            {/* Pricing card */}
            <div className="p-6 rounded-xl border flex-1" style={{ background: '#f0fdf4', borderColor: '#bbf7d0' }}>
              <p className="text-sm font-bold mb-3" style={{ color: '#052e16' }}>Pricing</p>

              <div className="mb-4">
                <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#16a34a' }}>Monthly</p>
                <p className="text-3xl font-bold mb-1" style={{ color: '#052e16' }}>
                  Starting at <span style={{ color: '#16a34a' }}>$499</span><span className="text-base font-normal" style={{ color: '#166534' }}>/mo</span>
                </p>
                <p className="text-xs leading-relaxed" style={{ color: '#166534' }}>
                  Monthly cost scales with call volume. Scoped after the core JSM build is confirmed.
                </p>
              </div>

              <div className="border-t pt-4 mt-4" style={{ borderColor: '#bbf7d0' }}>
                <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#16a34a' }}>Setup fee</p>
                <div className="flex items-center gap-2">
                  <span className="text-xl font-bold" style={{ color: '#052e16' }}>$0</span>
                  <span className="text-xs px-2 py-0.5 rounded font-bold" style={{ background: '#dcfce7', color: '#16a34a' }}>
                    if you're already a JSM client
                  </span>
                </div>
                <p className="text-xs mt-1 leading-relaxed" style={{ color: '#166534' }}>
                  No setup cost for existing clients. For new clients, setup is scoped separately.
                </p>
              </div>
            </div>

            {/* Note */}
            <div className="p-5 rounded-xl border" style={{ background: '#f0fdf4', borderColor: '#bbf7d0' }}>
              <div className="flex items-start gap-3">
                <SafeIcon icon={FiInfo} className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#16a34a' }} />
                <p className="text-xs leading-relaxed" style={{ color: '#166534' }}>
                  <strong style={{ color: '#052e16' }}>This is an optional add-on, not part of the core build.</strong> The main service is your JSM asset management and helpdesk system. AI voice intake is priced and scoped separately — we'll discuss it during discovery if phone-based intake makes sense for your field team.
                </p>
              </div>
            </div>

            <button onClick={() => scrollTo('booking')}
              className="group flex items-center justify-center gap-2 px-6 py-3.5 rounded font-bold text-sm"
              style={{ background: '#16a34a', color: '#ffffff' }}>
              Ask About AI Voice Intake
              <SafeIcon icon={FiArrowRight} className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HomeAIVoice;